import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { EllipsisVertical, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { components, type OptionProps } from 'react-select';
import { toast } from 'sonner';
import { z } from 'zod';

import { useGetAllClassesWithSearchInfinite } from '~entities/classes/api/get-all-classes-with-search';
import {
  BillingIntent,
  TransferCreditMode,
  TransferErrorCode,
  getRemainingCreditAPI,
  useGetRemainingCredit,
} from '~entities/classes/api/get-remaining-credit';
import {
  TRANSFER_PAYMENT_METHOD_NOT_FOUND_MESSAGE,
  type TransferClassResponse,
  useTransferClassMutation,
} from '~entities/classes/api/transfer-class';
import {
  formatEnrollmentSavedMethodLabel,
  pickEnrollmentCatalogDefaultMethod,
  useGetEnrollmentPaymentMethods,
} from '~entities/payment/api/get-enrollment-payment-methods';
import { useGetScheduledDrops } from '~entities/scheduled-drops/api/get-scheduled-drops';
import { type Enrollment } from '~entities/student/modal/student';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';
import { AgeRangeWarningDialog } from '~features/classes/ui/age-range-warning-dialog';

import { useGetCurrency } from '~shared/api/currency-api';
import { DropClassDialog } from '~shared/components/dialog/drop-class-dialog';
import { ScheduleDropDialog } from '~shared/components/dialog/schedule-drop-dialog';
import {
  formatAgeRange,
  formatClassTime,
  parseToNoonDate,
} from '~shared/helpers/datetime';
import { useDebounce } from '~shared/shadcn/expansions/multiple-selector';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent, CardTitle } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select as PaymentMethodSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';
import { isAgeInRange } from '~shared/utils/date-time';

import Select from '../select/select';

interface OptionType {
  isDisabled?: boolean;
  disabledReason?: string;
  isOutOfAgeRange?: boolean;

  value: string;
  label: string;
  startAgeMonth: number;
  startAgeYear: number;
  endAgeMonth: number;
  endAgeYear: number;
}

const CustomOption: React.FC<OptionProps<OptionType, false>> = (props) => {
  const option = props.data;
  const isOutOfAgeRange = option.isOutOfAgeRange ?? false;
  const ageRangeText = formatAgeRange(
    option.startAgeYear,
    option.startAgeMonth,
    option.endAgeYear,
    option.endAgeMonth,
  );
  const tooltipText = isOutOfAgeRange
    ? `Outside recommended age range (${ageRangeText}). Admin override available.`
    : props.label;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className=''>
            <components.Option {...props}>
              <span
                className={cn(isOutOfAgeRange && 'text-muted-foreground')}
              >
                {props.label}
              </span>
            </components.Option>
          </div>
        </TooltipTrigger>
        <TooltipContent side='right' sideOffset={20}>
          <p className='text-secondary'>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const classTransferSchema = z.object({
  newClass: z
    .object({
      value: z.string(),
      label: z.string(),
      startAgeMonth: z.number(),
      startAgeYear: z.number(),
      endAgeMonth: z.number(),
      endAgeYear: z.number(),
      registrationFeeAmount: z.number().optional(),
      costumeFee: z.number().optional(),
    })
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select a new class',
    }),
  billingIntent: z.nativeEnum(BillingIntent),
  skipPaymentMethod: z.boolean(),
  chargeMembershipFee: z.boolean(),
  chargeCostumeFee: z.boolean(),
  creditOption: z.enum([
    TransferCreditMode.AUTO,
    TransferCreditMode.CUSTOM,
    TransferCreditMode.NONE,
  ]),
  customCreditAmount: z.string().refine(
    (val) => {
      const num = parseFloat(val);
      return !isNaN(num) && num >= 0;
    },
    {
      message: 'Credit amount must be a valid positive number',
    },
  ),
});

const isPaymentMethodRequiredTransferResponse = (
  response?: TransferClassResponse | null,
) =>
  response?.message === TRANSFER_PAYMENT_METHOD_NOT_FOUND_MESSAGE &&
  response.transferCompleted === false &&
  response.sourceEnrollmentPreserved === true;

const showPaymentMethodRequiredTransferToast = () => {
  toast.error(
    'Parent needs a saved payment method before this transfer can complete. Source class was not changed.',
  );
};

interface TransferClassErrorResponse {
  error?: string | { message?: string };
  message?: string | string[];
}

const getTransferClassErrorData = (
  error: unknown,
): TransferClassErrorResponse | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const maybeError = error as {
    response?: { data?: TransferClassErrorResponse };
  };

  return maybeError.response?.data;
};

const getTransferClassErrorCode = (error: unknown): string | undefined => {
  const errorData = getTransferClassErrorData(error);
  if (typeof errorData?.error === 'string') {
    return errorData.error;
  }
  return undefined;
};

const getTransferClassErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const errorData = getTransferClassErrorData(error);
  const nestedMessage =
    errorData?.error && typeof errorData.error === 'object'
      ? errorData.error.message
      : undefined;
  const topLevelMessage = Array.isArray(errorData?.message)
    ? errorData.message.join(', ')
    : errorData?.message;

  return nestedMessage ?? topLevelMessage ?? fallback;
};

const isCreditMismatchError = (error: unknown) =>
  getTransferClassErrorCode(error) === TransferErrorCode.CREDIT_MISMATCH;

const didTransferSucceed = (
  response: TransferClassResponse | null | undefined,
  isV2: boolean,
) => {
  if (!response) {
    return false;
  }
  // V2 always returns an explicit transferred flag.
  if (isV2) {
    return (
      response.transferred === true || response.transferCompleted === true
    );
  }
  // V1 treats a non-throwing response as success (except PM-required shape).
  return true;
};
type FormData = z.infer<typeof classTransferSchema>;

export interface ClassDetailsCardProps {
  enrollment: Enrollment;
  studentName: string;
  studentAge: {
    years: number;
    months: number;
  };
  studioId: string;
  studentId: string;
  parentId?: string;
}

export function ClassDetailsCard({
  enrollment,
  studentName,
  studentAge,
  studioId,
  studentId,
  parentId = '',
}: ClassDetailsCardProps) {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const { hasAdminAccess } = useCurrentUserRole();
  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(classTransferSchema),
    defaultValues: {
      newClass: undefined,
      billingIntent: BillingIntent.NO_CHANGE,
      skipPaymentMethod: false,
      chargeMembershipFee: false,
      chargeCostumeFee: false,
      creditOption: TransferCreditMode.AUTO,
      customCreditAmount: '0',
    },
  });

  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [resolvedPaymentMethodId, setResolvedPaymentMethodId] = useState<
    string | null
  >(null);
  const [showDropDialog, setShowDropDialog] = useState(false);
  const [showScheduleDropDialog, setShowScheduleDropDialog] = useState(false);
  const [ageRangeWarningOpen, setAgeRangeWarningOpen] = useState(false);
  const [ageRangeWarningClass, setAgeRangeWarningClass] = useState<
    FormData['newClass'] | null
  >(null);

  // Watch the billing intent and credit option to determine which fields to enable
  const billingIntent = watch('billingIntent');
  const creditOption = watch('creditOption');
  const customCreditAmount = watch('customCreditAmount');
  const skipPaymentMethodWatch = watch('skipPaymentMethod');
  const chargeMembershipFeeWatch = watch('chargeMembershipFee');
  const chargeCostumeFeeWatch = watch('chargeCostumeFee');
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const selectedNewClass = watch('newClass');
  const selectedNewClassId = selectedNewClass?.value;
  const destMembershipFee = Number(selectedNewClass?.registrationFeeAmount ?? 0);
  const destCostumeFee = Number(selectedNewClass?.costumeFee ?? 0);

  // Active classes only (exclude archived + past / end-dated sessions) — ENR-3676
  const [transferClassSearch, setTransferClassSearch] = useState('');
  const debouncedTransferClassSearch = useDebounce(transferClassSearch, 500);
  const currentClassId = enrollment.enrollmentId?._id;
  const {
    data: transferClassesPages,
    isFetching: isFetchingTransferClasses,
    isPending: isPendingTransferClasses,
    isFetchingNextPage: isFetchingMoreTransferClasses,
    hasNextPage: hasMoreTransferClasses,
    fetchNextPage: fetchMoreTransferClasses,
  } = useGetAllClassesWithSearchInfinite(
    {
      name: debouncedTransferClassSearch,
      enforceStatusFilters: true,
      limit: 50,
    },
    { enabled: showTransferDialog },
  );
  const transferClassOptions = useMemo(() => {
    const pages = transferClassesPages?.pages ?? [];
    const seen = new Set<string>();
    const options: Array<{
      value: string;
      label: string;
      startAgeMonth: number;
      startAgeYear: number;
      endAgeMonth: number;
      endAgeYear: number;
      registrationFeeAmount: number;
      costumeFee: number;
      isOutOfAgeRange: boolean;
    }> = [];

    for (const page of pages) {
      for (const classItem of page.data.classes) {
        if (classItem._id === currentClassId || seen.has(classItem._id)) {
          continue;
        }
        seen.add(classItem._id);

        const isOutOfAgeRange = !isAgeInRange({
          age: {
            months: studentAge.months,
            years: studentAge.years,
          },
          startAge: {
            months: classItem.startMonth,
            years: classItem.startYear,
          },
          endAge: {
            months: classItem.endMonth,
            years: classItem.endYear,
          },
        });

        options.push({
          value: classItem._id,
          label: classItem.title,
          startAgeMonth: classItem.startMonth,
          startAgeYear: classItem.startYear,
          endAgeMonth: classItem.endMonth,
          endAgeYear: classItem.endYear,
          registrationFeeAmount: classItem.registrationFeeAmount ?? 0,
          costumeFee: classItem.costumeFee ?? 0,
          isOutOfAgeRange,
        });
      }
    }

    return options;
  }, [
    transferClassesPages?.pages,
    currentClassId,
    studentAge.months,
    studentAge.years,
  ]);

  const { data: studioDetails } = useGetStudioDetails();
  const isProrationDisabled = studioDetails?.prorationEnabled === false;
  const isV2Transfer = studioDetails?.isV2 === true;
  // V2 transfer APIs require Mongo studio `_id` (JWT locationId is also Mongo id).
  const mongoStudioId = studioDetails?._id || studioId;
  const transferCreditMode =
    billingIntent === BillingIntent.IMMEDIATE ? creditOption : TransferCreditMode.NONE;
  const parsedCustomCreditAmount = parseFloat(customCreditAmount);

  const {
    data: enrollmentPaymentMethods,
    isLoading: isLoadingEnrollmentPms,
  } = useGetEnrollmentPaymentMethods({
    parentId,
    enabled: showTransferDialog && isV2Transfer && !!parentId,
  });

  // Mirror enrollment: auto-select catalog default (or first saved) PM for V2.
  useEffect(() => {
    if (!showTransferDialog || !isV2Transfer || skipPaymentMethodWatch) {
      return;
    }
    if (resolvedPaymentMethodId) {
      const stillPresent = enrollmentPaymentMethods?.savedMethods.some(
        (method) => method.id === resolvedPaymentMethodId,
      );
      if (stillPresent) return;
    }
    const defaultMethod = pickEnrollmentCatalogDefaultMethod(
      enrollmentPaymentMethods,
    );
    setResolvedPaymentMethodId(defaultMethod?.id ?? null);
  }, [
    enrollmentPaymentMethods,
    isV2Transfer,
    resolvedPaymentMethodId,
    showTransferDialog,
    skipPaymentMethodWatch,
  ]);

  const transferPaymentMethodId =
    isV2Transfer && !skipPaymentMethodWatch
      ? (resolvedPaymentMethodId ?? undefined)
      : undefined;
  const transferPaymentProvider =
    isV2Transfer && !skipPaymentMethodWatch
      ? enrollmentPaymentMethods?.paymentProvider
      : undefined;

  const { data: remainingCreditData, error: remainingCreditError } =
    useGetRemainingCredit({
      studioId: mongoStudioId,
      currentClassId: enrollment.enrollmentId?._id ?? '',
      newClassId: selectedNewClassId,
      studentId: studentId,
      billingIntent: billingIntent,
      creditMode: transferCreditMode,
      ...(creditOption === TransferCreditMode.CUSTOM && !Number.isNaN(parsedCustomCreditAmount)
        ? { customCreditAmount: parsedCustomCreditAmount }
        : {}),
      skipPaymentMethod: skipPaymentMethodWatch,
      chargeMembershipFee: chargeMembershipFeeWatch,
      chargeCostumeFee: chargeCostumeFeeWatch,
      ...(transferPaymentMethodId
        ? { paymentMethodId: transferPaymentMethodId }
        : {}),
      ...(transferPaymentProvider
        ? { paymentProvider: transferPaymentProvider }
        : {}),
      isV2: isV2Transfer,
    });

  // When proration is off, immediate recalculation is not available — lock UI to no_change.
  useEffect(() => {
    if (isProrationDisabled && billingIntent !== BillingIntent.NO_CHANGE) {
      setValue('billingIntent', BillingIntent.NO_CHANGE);
      setValue('creditOption', TransferCreditMode.AUTO);
    }
  }, [isProrationDisabled, billingIntent, setValue]);

  const transferClassMutation = useTransferClassMutation();

  const { data: scheduledDrops } = useGetScheduledDrops({
    enrollmentId: enrollment.enrollmentId?._id,
    studentId,
    status: 'pending',
  });
  const pendingScheduledDrop = scheduledDrops?.[0];

  const classTitle = enrollment.enrollmentId?.title ?? '';
  const isArchivedClass = enrollment.enrollmentId?.isArchive === true;

  // const startDate = format(new Date(startDateString), 'dd/MM/yyyy');
  // const endDate = endDateString
  //   ? format(new Date(endDateString), 'dd/MM/yyyy')
  //   : 'N/A';

  const startDateString = parseToNoonDate(enrollment.enrollmentId?.startDate);
  const endDateString = parseToNoonDate(enrollment.enrollmentId?.endDate);
  const enrolledDateString = parseToNoonDate(enrollment.enrolledDate);

  // Time strings may be `HH:mm`, full ISO ("2025-02-16T07:30:00.000Z"), or
  // missing entirely when the underlying class doc is orphaned/deleted or was
  // partially written. `formatClassTime` handles all of those without throwing.
  const formattedStartTime = formatClassTime(enrollment.enrollmentId?.startTime);
  const formattedEndTime = formatClassTime(enrollment.enrollmentId?.endTime);

  const time =
    formattedStartTime && formattedEndTime
      ? `${formattedStartTime} - ${formattedEndTime}`
      : '-';

  // Runtime class docs can be orphaned/partial even if the generated type marks instructor as present.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const teacherName = enrollment.enrollmentId?.instructor?.[0]?.fieldName ?? '';

  const classDays = enrollment.enrollmentId?.days;

  const handleTransferClass = async () => {
    if (!enrollment.enrollmentId?._id || !isValid || !transferCalculations) {
      return;
    }

    if (!mongoStudioId) {
      toast.error('Studio context is required to transfer a class.');
      return;
    }

    const currentClassId = enrollment.enrollmentId._id;
    const newClassId = getValues('newClass').value;
    const billingIntentValue = getValues('billingIntent');
    const skipPaymentMethod = getValues('skipPaymentMethod');
    const creditOptionValue = getValues('creditOption');
    const creditMode =
      billingIntentValue === BillingIntent.IMMEDIATE
        ? creditOptionValue
        : TransferCreditMode.NONE;

    if (isProrationDisabled && billingIntentValue === BillingIntent.IMMEDIATE) {
      toast.error(
        'Recalculate immediately is not available when proration is disabled. Choose "Keep current month unchanged", or enable proration in Billing Settings.',
      );
      return;
    }

    if (
      isV2Transfer &&
      !skipPaymentMethod &&
      !resolvedPaymentMethodId?.trim()
    ) {
      toast.error(
        'Please select a saved payment method, or check "Post charges to the account; parent will pay later".',
      );
      return;
    }

    const paymentMethodIdForTransfer =
      isV2Transfer && !skipPaymentMethod
        ? resolvedPaymentMethodId ?? undefined
        : undefined;
    const paymentProviderForTransfer =
      isV2Transfer && !skipPaymentMethod
        ? enrollmentPaymentMethods?.paymentProvider
        : undefined;

    try {
      let transferResponse: TransferClassResponse;

      const chargeMembershipFee = getValues('chargeMembershipFee') === true;
      const chargeCostumeFee = getValues('chargeCostumeFee') === true;

      if (billingIntentValue === BillingIntent.NO_CHANGE) {
        transferResponse = await transferClassMutation.mutateAsync({
          studioId: mongoStudioId,
          currentClassId,
          newClassId,
          studentId: studentId,
          billingIntent: BillingIntent.NO_CHANGE,
          creditMode: TransferCreditMode.NONE,
          skipPaymentMethod,
          chargeMembershipFee,
          chargeCostumeFee,
          isV2: isV2Transfer,
          noCreditRequired: true,
          newCreditAmount: 0,
          ...(paymentMethodIdForTransfer
            ? { paymentMethodId: paymentMethodIdForTransfer }
            : {}),
          ...(paymentProviderForTransfer
            ? { paymentProvider: paymentProviderForTransfer }
            : {}),
        });
      } else {
        let customCreditAmountValue: number | undefined;
        if (creditOptionValue === TransferCreditMode.CUSTOM) {
          customCreditAmountValue = parseFloat(getValues('customCreditAmount'));
        }

        transferResponse = await transferClassMutation.mutateAsync({
          studioId: mongoStudioId,
          currentClassId,
          newClassId,
          studentId: studentId,
          billingIntent: BillingIntent.IMMEDIATE,
          creditMode,
          ...(customCreditAmountValue !== undefined
            ? { customCreditAmount: customCreditAmountValue }
            : {}),
          skipPaymentMethod,
          chargeMembershipFee,
          chargeCostumeFee,
          availableCredit: transferCalculations.availableCredit,
          creditAppliedToNewClass: transferCalculations.creditUsedForTransfer,
          creditAddedToWallet: transferCalculations.creditToBeAddedToWallet,
          isV2: isV2Transfer,
          noCreditRequired: creditOptionValue === TransferCreditMode.NONE,
          newCreditAmount:
            creditOptionValue === TransferCreditMode.NONE
              ? 0
              : creditOptionValue === TransferCreditMode.CUSTOM
                ? transferCalculations.creditUsedForTransfer
                : transferCalculations.availableCredit,
          ...(paymentMethodIdForTransfer
            ? { paymentMethodId: paymentMethodIdForTransfer }
            : {}),
          ...(paymentProviderForTransfer
            ? { paymentProvider: paymentProviderForTransfer }
            : {}),
        });
      }

      if (isPaymentMethodRequiredTransferResponse(transferResponse)) {
        showPaymentMethodRequiredTransferToast();
        return;
      }

      if (!didTransferSucceed(transferResponse, isV2Transfer)) {
        toast.error(
          transferResponse.message ??
            'Transfer did not complete. Source class was not changed.',
        );
        return;
      }

      toast.success(
        transferResponse.message || 'Class transferred successfully',
      );
      setShowTransferDialog(false);
    } catch (error: unknown) {
      if (
        isCreditMismatchError(error) &&
        billingIntentValue === BillingIntent.IMMEDIATE
      ) {
        console.warn(
          'Credit mismatch detected, refreshing calculations and retrying...',
        );
        toast.info('Credit calculations changed, refreshing and retrying...');

        try {
          const freshCreditData = await getRemainingCreditAPI({
            studioId: mongoStudioId,
            currentClassId,
            newClassId,
            studentId,
            billingIntent: BillingIntent.IMMEDIATE,
            creditMode,
            ...(creditOptionValue === TransferCreditMode.CUSTOM
              ? {
                  customCreditAmount: parseFloat(
                    getValues('customCreditAmount'),
                  ),
                }
              : {}),
            skipPaymentMethod,
            isV2: isV2Transfer,
            ...(paymentMethodIdForTransfer
              ? { paymentMethodId: paymentMethodIdForTransfer }
              : {}),
            ...(paymentProviderForTransfer
              ? { paymentProvider: paymentProviderForTransfer }
              : {}),
          });

          let freshAvailableCredit = freshCreditData.remainingCredit;
          let freshCreditUsed = freshCreditData.creditUsedForTransfer;
          let freshCreditToWallet = freshCreditData.creditToBeAddedToWallet;
          let freshCustomCreditAmount: number | undefined;

          if (creditOptionValue === TransferCreditMode.CUSTOM) {
            const customAmount = Math.max(
              0,
              parseFloat(getValues('customCreditAmount')) || 0,
            );
            freshCustomCreditAmount = customAmount;
            // Keep availableCredit as the full server pool; custom is apply amount only.
            freshCreditUsed = Math.min(
              customAmount,
              freshAvailableCredit,
              freshCreditData.newTuitionFee,
            );
            freshCreditToWallet = Math.max(
              0,
              freshAvailableCredit - freshCreditUsed,
            );
          } else if (creditOptionValue === TransferCreditMode.NONE) {
            freshAvailableCredit = 0;
            freshCreditUsed = 0;
            freshCreditToWallet = 0;
          }

          const retryResponse = await transferClassMutation.mutateAsync({
            studioId: mongoStudioId,
            currentClassId,
            newClassId,
            studentId: studentId,
            billingIntent: BillingIntent.IMMEDIATE,
            creditMode,
            ...(freshCustomCreditAmount !== undefined
              ? { customCreditAmount: freshCustomCreditAmount }
              : {}),
            skipPaymentMethod,
            chargeMembershipFee: getValues('chargeMembershipFee') === true,
            chargeCostumeFee: getValues('chargeCostumeFee') === true,
            availableCredit: freshAvailableCredit,
            creditAppliedToNewClass: freshCreditUsed,
            creditAddedToWallet: freshCreditToWallet,
            isV2: isV2Transfer,
            noCreditRequired: creditOptionValue === TransferCreditMode.NONE,
            newCreditAmount:
              creditOptionValue === TransferCreditMode.NONE
                ? 0
                : creditOptionValue === TransferCreditMode.CUSTOM
                  ? freshCreditUsed
                  : freshAvailableCredit,
            ...(paymentMethodIdForTransfer
              ? { paymentMethodId: paymentMethodIdForTransfer }
              : {}),
            ...(paymentProviderForTransfer
              ? { paymentProvider: paymentProviderForTransfer }
              : {}),
          });

          if (isPaymentMethodRequiredTransferResponse(retryResponse)) {
            showPaymentMethodRequiredTransferToast();
            return;
          }

          if (!didTransferSucceed(retryResponse, isV2Transfer)) {
            toast.error(
              retryResponse.message ??
                'Transfer did not complete. Source class was not changed.',
            );
            return;
          }

          toast.success(
            retryResponse.message || 'Class transferred successfully',
          );
          setShowTransferDialog(false);
        } catch (retryError: unknown) {
          const errorMessage = getTransferClassErrorMessage(
            retryError,
            'Failed to transfer class after retry',
          );
          toast.error(errorMessage);
          console.error('Error transferring class after retry:', retryError);
        }
      } else {
        const errorMessage = getTransferClassErrorMessage(
          error,
          'Failed to transfer class',
        );
        toast.error(errorMessage);
        console.error('Error transferring class:', error);
      }
    }
  };

  const isClassOutOfAgeRange = (newClass: FormData['newClass']) => {
    return !isAgeInRange({
      age: {
        months: studentAge.months,
        years: studentAge.years,
      },
      startAge: {
        months: newClass.startAgeMonth,
        years: newClass.startAgeYear,
      },
      endAge: {
        months: newClass.endAgeMonth,
        years: newClass.endAgeYear,
      },
    });
  };

  const handleTransferSaveClick = (data: FormData) => {
    if (isClassOutOfAgeRange(data.newClass)) {
      setAgeRangeWarningClass(data.newClass);
      setAgeRangeWarningOpen(true);
      return;
    }

    void handleTransferClass();
  };

  const handleAgeRangeWarningConfirm = () => {
    setAgeRangeWarningOpen(false);
    setAgeRangeWarningClass(null);
    void handleTransferClass();
  };

  const handleAgeRangeWarningClose = (open: boolean) => {
    setAgeRangeWarningOpen(open);
    if (!open) {
      setAgeRangeWarningClass(null);
    }
  };

  // Calculate transfer accounting details
  const transferCalculations = useMemo(() => {
    if (!remainingCreditData) {
      return null;
    }

    const newTuitionFee = remainingCreditData.newTuitionFee;
    const currentTuitionFee = remainingCreditData.currentTuitionFee;
    const previewSummary = remainingCreditData.previewSummary;
    const discountLines = remainingCreditData.discountLines ?? [];
    const discountTotal = remainingCreditData.discountTotal ?? 0;
    const useEnrollmentAmountDue =
      remainingCreditData.amountDueFromEnrollmentPreview === true;

    // When 'no_change' is selected, credit values are 0 from backend
    // We show the previewSummary instead
    if (billingIntent === BillingIntent.NO_CHANGE) {
      return {
        currentTuitionFee,
        newTuitionFee,
        availableCredit: 0,
        effectiveCredit: 0,
        creditUsedForTransfer: 0,
        creditToBeAddedToWallet: 0,
        amountToCharge: useEnrollmentAmountDue
          ? remainingCreditData.amountToBeCharged
          : 0,
        discountLines,
        discountTotal,
        previewSummary,
        billingIntent: BillingIntent.NO_CHANGE,
      };
    }

    // Existing logic for 'immediate' billing intent
    const autoCredit = remainingCreditData.remainingCredit;
    let availableCredit = autoCredit;
    let creditUsedForTransfer = 0;
    let creditToBeAddedToWallet = 0;
    let amountToCharge = 0;

    // Determine credit split based on selected option.
    // Custom amount = how much to APPLY to the new class (not the available pool).
    switch (creditOption) {
      case TransferCreditMode.AUTO:
        creditUsedForTransfer = remainingCreditData.creditUsedForTransfer;
        creditToBeAddedToWallet = remainingCreditData.creditToBeAddedToWallet;
        amountToCharge = remainingCreditData.amountToBeCharged;
        break;
      case TransferCreditMode.CUSTOM: {
        const customAmount = Math.max(0, parseFloat(customCreditAmount) || 0);
        creditUsedForTransfer = Math.min(
          customAmount,
          autoCredit,
          newTuitionFee,
        );
        creditToBeAddedToWallet = Math.max(
          0,
          autoCredit - creditUsedForTransfer,
        );
        // Prefer enrollment preview (includes discounts) when V2 returned it.
        amountToCharge = useEnrollmentAmountDue
          ? remainingCreditData.amountToBeCharged
          : Math.max(0, newTuitionFee - creditUsedForTransfer);
        break;
      }
      case TransferCreditMode.NONE:
        availableCredit = 0;
        creditUsedForTransfer = 0;
        creditToBeAddedToWallet = 0;
        // Do not use raw list tuition — enrollment preview includes discounts.
        amountToCharge = useEnrollmentAmountDue
          ? remainingCreditData.amountToBeCharged
          : newTuitionFee;
        break;
    }

    return {
      currentTuitionFee,
      newTuitionFee,
      /** Full transferable credit pool (server availableCredit). */
      availableCredit,
      /** @deprecated alias — applied credit for auto; prefer creditUsedForTransfer */
      effectiveCredit: creditUsedForTransfer,
      creditUsedForTransfer,
      creditToBeAddedToWallet,
      amountToCharge,
      discountLines,
      discountTotal,
      previewSummary,
      billingIntent: BillingIntent.IMMEDIATE,
    };
  }, [
    billingIntent,
    creditOption,
    customCreditAmount,
    remainingCreditData,
  ]);

  return (
    <>
      <Card className={cn('min-h-[19.625rem] w-[21.125rem]')}>
        <CardContent className='mt-6'>
          <div className='flex items-start justify-between gap-2'>
            <div className='min-w-0 flex-1'>
              {isArchivedClass ? (
                <Badge
                  variant='secondary'
                  className='mb-2 w-fit text-xs font-normal'
                >
                  Archived class
                </Badge>
              ) : null}
              <CardTitle
                className={cn(
                  'mb-2 text-lg leading-[1.688rem]',
                  !isArchivedClass && 'mt-3.5',
                )}
              >
                {classTitle}
              </CardTitle>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger disabled={!hasAdminAccess} asChild>
                <Button variant='ghost' className='px-2.5'>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='w-56' align='start'>
                <DropdownMenuLabel>Class Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => {
                      setShowTransferDialog(true);
                    }}
                  >
                    Transfer Class
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setShowDropDialog(true);
                    }}
                  >
                    Drop Class
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setShowScheduleDropDialog(true);
                    }}
                  >
                    Schedule Drop
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className={cn('mb-2 text-sm leading-[1.313rem] text-black')}>
            {time}
          </p>
          <ul className={cn('flex gap-2.5')}>
            {classDays?.map((day) => (
              <li key={day}>
                <Badge
                  className={cn(
                    'flex items-center justify-center px-1 text-xs',
                  )}
                >
                  {day}
                </Badge>
              </li>
            ))}
          </ul>

          <div className={cn('mt-6 flex justify-between')}>
            <div className={cn('flex flex-col gap-6')}>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Main Teacher
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {teacherName}
                </p>
              </div>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Class Starts
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {startDateString
                    ? format(startDateString, 'MMMM d, yyyy')
                    : '-'}
                </p>
              </div>
            </div>

            <div className={cn('flex flex-col gap-6')}>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Enrolled Date
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {enrolledDateString
                    ? format(enrolledDateString, 'MMMM d, yyyy')
                    : '-'}
                </p>
              </div>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Class End
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {endDateString ? format(endDateString, 'MMMM d, yyyy') : '-'}
                </p>
              </div>
            </div>
          </div>

          {pendingScheduledDrop && (() => {
            const scheduledDropDate = parseToNoonDate(
              pendingScheduledDrop.scheduledDropDate,
            );
            // `parseToNoonDate` can return an Invalid Date for malformed
            // inputs (truthy but `.getTime()` is NaN), which would still
            // throw RangeError when passed to `format(...)`.
            if (
              !scheduledDropDate ||
              Number.isNaN(scheduledDropDate.getTime())
            ) {
              return null;
            }
            return (
              <div className='mt-4 rounded-md bg-red-50 px-3 py-2 text-center'>
                <p className='text-sm font-bold text-red-700'>
                  Scheduled Drop:{' '}
                  {format(scheduledDropDate, 'MMMM d, yyyy')}
                </p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {showDropDialog && enrollment.enrollmentId?._id && (
        <DropClassDialog
          open={showDropDialog}
          onOpenChange={setShowDropDialog}
          classId={enrollment.enrollmentId._id}
          studentId={studentId}
          studioId={studioId}
        />
      )}

      {showScheduleDropDialog && enrollment.enrollmentId?._id && (
        <ScheduleDropDialog
          open={showScheduleDropDialog}
          onOpenChange={setShowScheduleDropDialog}
          enrollmentId={enrollment.enrollmentId._id}
          studentId={studentId}
          classStartDate={enrollment.enrollmentId.startDate}
          classEndDate={enrollment.enrollmentId.endDate}
        />
      )}

      <Dialog
        open={showTransferDialog}
        onOpenChange={(open) => {
          setShowTransferDialog(open);
          if (!open) {
            setAgeRangeWarningOpen(false);
            setAgeRangeWarningClass(null);
            setResolvedPaymentMethodId(null);
            setTransferClassSearch('');
            reset({
              newClass: undefined,
              billingIntent: BillingIntent.NO_CHANGE,
              skipPaymentMethod: false,
              chargeMembershipFee: false,
              chargeCostumeFee: false,
              creditOption: TransferCreditMode.AUTO,
              customCreditAmount: '0',
            });
          }
        }}
      >
        <DialogContent className='max-h-[95vh] overflow-visible sm:max-w-[425px] lg:max-w-[600px]'>
          <DialogHeader>
            <DialogTitle>Transfer Class</DialogTitle>
            <DialogDescription>
              Transfer Class to another slot. Click save when you&apos;re done.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='mb-1 mt-4 grid grid-cols-2 gap-2'>
              <div>
                <Label>Current Class</Label>
                <Input value={classTitle} disabled className='flex-1' />
              </div>
              <div>
                <Label>New Class</Label>
                <Controller
                  name='newClass'
                  control={control}
                  render={({ field }) => (
                    <Select
                      className='w-full flex-1'
                      menuPortalTarget={null}
                      options={transferClassOptions}
                      value={field.value}
                      onChange={field.onChange}
                      isLoading={
                        isPendingTransferClasses ||
                        (isFetchingTransferClasses &&
                          !isFetchingMoreTransferClasses)
                      }
                      isMenuListFetchingMore={isFetchingMoreTransferClasses}
                      onMenuScrollToBottom={() => {
                        if (
                          hasMoreTransferClasses &&
                          !isFetchingMoreTransferClasses
                        ) {
                          void fetchMoreTransferClasses();
                        }
                      }}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setTransferClassSearch(inputValue);
                        }
                      }}
                      filterOption={null}
                      components={{
                        Option:
                          CustomOption as unknown as React.ComponentType<OptionProps>,
                      }}
                    />
                  )}
                />
                {errors.newClass && (
                  <p className='mt-1 text-sm text-red-500'>
                    {errors.newClass.message}
                  </p>
                )}
              </div>
            </div>

            {/* Studio Proration Setting Notice */}
            <div className='rounded-md border bg-muted/50 p-3'>
              <div className='flex items-center justify-between'>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>
                    Studio proration setting:{' '}
                  </span>
                  <span
                    className={cn(
                      'font-medium',
                      studioDetails?.prorationEnabled !== false
                        ? 'text-green-600'
                        : 'text-amber-600',
                    )}
                  >
                    {studioDetails?.prorationEnabled !== false
                      ? 'Enabled'
                      : 'Disabled'}
                  </span>
                </div>
                <Link
                  to='/payment/payment-settings'
                  className='text-xs text-primary underline-offset-4 hover:underline'
                  onClick={() => {
                    setShowTransferDialog(false);
                  }}
                >
                  Billing Settings
                </Link>
              </div>
              {isProrationDisabled && (
                <p className='mt-1 text-xs text-amber-600'>
                  Proration is disabled for this studio. Recalculate immediately
                  and credit options are unavailable. Transfers use &quot;Keep
                  current month unchanged&quot; only. Enable proration in Billing
                  Settings to unlock immediate recalculation.
                </p>
              )}
            </div>

            {/* Billing Intent Selection */}
            <div className='my-1'>
              <Label className='mb-2 block font-semibold'>
                How should tuition be handled?
              </Label>
              <Controller
                name='billingIntent'
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className='space-y-3'
                    disabled={isProrationDisabled}
                  >
                    <div className='flex items-start space-x-2'>
                      <RadioGroupItem
                        value={BillingIntent.NO_CHANGE}
                        id='billing-no-change'
                        className='mt-1'
                      />
                      <div>
                        <Label
                          htmlFor='billing-no-change'
                          className='cursor-pointer'
                        >
                          Keep current month unchanged
                          <Badge variant='secondary' className='ml-2 text-xs'>
                            Recommended
                          </Badge>
                        </Label>
                        <span className='block text-sm text-muted-foreground'>
                          No immediate billing changes. New class billing starts
                          next billing cycle.
                        </span>
                      </div>
                    </div>

                    <div
                      className={`flex items-start space-x-2 ${isProrationDisabled ? 'opacity-50' : ''}`}
                    >
                      <RadioGroupItem
                        value={BillingIntent.IMMEDIATE}
                        id='billing-immediate'
                        className='mt-1'
                        disabled={isProrationDisabled}
                      />
                      <div>
                        <Label
                          htmlFor='billing-immediate'
                          className={
                            isProrationDisabled
                              ? 'cursor-not-allowed'
                              : 'cursor-pointer'
                          }
                        >
                          Recalculate immediately
                        </Label>
                        <span className='block text-sm text-muted-foreground'>
                          {isProrationDisabled
                            ? 'Unavailable while studio proration is disabled.'
                            : 'Prorate and apply credit immediately for the current billing period.'}
                        </span>
                      </div>
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            {/* Credit Options - Only shown when billingIntent is 'immediate' */}
            {billingIntent === BillingIntent.IMMEDIATE && !isProrationDisabled && (
              <div className='my-1 flex items-center gap-2'>
                <Controller
                  name='creditOption'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className='space-y-3'
                    >
                      <div className='flex items-center space-x-2'>
                        <RadioGroupItem value={TransferCreditMode.AUTO} id='dialog-credit-auto' />
                        <Label
                          htmlFor='dialog-credit-auto'
                          className='cursor-pointer'
                        >
                          Auto-Calculated Credit:{' '}
                          {remainingCreditData?.remainingCredit.toFixed(2) ?? 0}
                          <span className='block text-sm text-muted-foreground'>
                            Based on unused days from the previous class
                          </span>
                        </Label>
                      </div>

                      <div className='flex items-center space-x-2'>
                        <RadioGroupItem
                          value={TransferCreditMode.CUSTOM}
                          id='dialog-credit-custom'
                        />
                        <div className='flex items-center gap-2'>
                          <Label
                            htmlFor='dialog-credit-custom'
                            className='cursor-pointer'
                          >
                            Custom Credit Amount
                          </Label>
                          <Controller
                            name='customCreditAmount'
                            control={control}
                            render={({ field }) => (
                              <Input
                                id='dialog-custom-credit-amount'
                                type='number'
                                {...field}
                                onChange={field.onChange}
                                className='ml-6 w-32'
                                placeholder='0'
                                disabled={creditOption !== TransferCreditMode.CUSTOM}
                              />
                            )}
                          />
                          {errors.customCreditAmount && (
                            <p className='ml-6 text-sm text-red-500'>
                              {errors.customCreditAmount.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className='flex items-center space-x-2'>
                        <RadioGroupItem value={TransferCreditMode.NONE} id='dialog-credit-none' />
                        <Label
                          htmlFor='dialog-credit-none'
                          className='cursor-pointer'
                        >
                          Do Not Apply Previous Class Credit
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                />
              </div>
            )}

            {isV2Transfer ? (
              <div className='grid gap-2'>
                <Label>Payment Method</Label>
                {isLoadingEnrollmentPms ? (
                  <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <Loader2 className='size-3.5 animate-spin' />
                    Loading saved payment methods…
                  </p>
                ) : (enrollmentPaymentMethods?.savedMethods.length ?? 0) > 0 ? (
                  <div className='space-y-2'>
                    <p className='text-sm font-medium text-muted-foreground'>
                      Saved payment methods
                    </p>
                    <PaymentMethodSelect
                      value={resolvedPaymentMethodId ?? ''}
                      disabled={skipPaymentMethodWatch}
                      onValueChange={(id) => {
                        const selected =
                          enrollmentPaymentMethods?.savedMethods.find(
                            (method) => method.id === id,
                          );
                        if (!selected) return;
                        setValue('skipPaymentMethod', false);
                        setResolvedPaymentMethodId(selected.id);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select a saved payment method' />
                      </SelectTrigger>
                      <SelectContent>
                        {enrollmentPaymentMethods?.savedMethods.map(
                          (method) => (
                            <SelectItem key={method.id} value={method.id}>
                              {formatEnrollmentSavedMethodLabel(method)}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </PaymentMethodSelect>
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No saved payment methods for this parent. Save a card
                    first, or check &quot;Post charges to the account; parent
                    will pay later&quot;.
                  </p>
                )}
              </div>
            ) : null}

            {isV2Transfer && (destMembershipFee > 0 || destCostumeFee > 0) ? (
              <div className='space-y-3 rounded-md border bg-muted/30 p-3'>
                <div>
                  <Label className='text-sm font-semibold'>
                    Destination class fees
                  </Label>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    Optional flat fees for the new class. They are not prorated
                    and are not affected by tuition credit or &quot;keep current
                    month unchanged&quot;.
                  </p>
                </div>
                {destMembershipFee > 0 ? (
                  <Controller
                    name='chargeMembershipFee'
                    control={control}
                    render={({ field }) => (
                      <div className='flex items-start space-x-2'>
                        <Checkbox
                          id='transfer-charge-membership'
                          className='mt-0.5'
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true);
                          }}
                        />
                        <Label
                          htmlFor='transfer-charge-membership'
                          className='cursor-pointer text-sm'
                        >
                          Charge membership fee (${destMembershipFee.toFixed(2)})
                        </Label>
                      </div>
                    )}
                  />
                ) : null}
                {destCostumeFee > 0 ? (
                  <Controller
                    name='chargeCostumeFee'
                    control={control}
                    render={({ field }) => (
                      <div className='flex items-start space-x-2'>
                        <Checkbox
                          id='transfer-charge-costume'
                          className='mt-0.5'
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true);
                          }}
                        />
                        <Label
                          htmlFor='transfer-charge-costume'
                          className='cursor-pointer text-sm'
                        >
                          Charge costume fee (${destCostumeFee.toFixed(2)})
                        </Label>
                      </div>
                    )}
                  />
                ) : null}
              </div>
            ) : null}

            <Controller
              name='skipPaymentMethod'
              control={control}
              render={({ field }) => (
                <div className='rounded-md border bg-muted/30 p-3'>
                  <div className='flex items-start space-x-2'>
                    <Checkbox
                      id='transfer-skip-payment-method'
                      className='mt-0.5'
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked === true);
                      }}
                    />
                    <div>
                      <Label
                        htmlFor='transfer-skip-payment-method'
                        className='cursor-pointer text-sm'
                      >
                        Post charges to the account; parent will pay later
                      </Label>
                      <p className='mt-1 text-xs text-muted-foreground'>
                        No payment will be attempted. Any charges created by
                        this transfer will remain as a pending outstanding
                        balance.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            />

            {remainingCreditError && selectedNewClassId ? (
              <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
                {getTransferClassErrorMessage(
                  remainingCreditError,
                  'Unable to load transfer billing preview.',
                )}
              </div>
            ) : null}

            {/* Transfer Calculation Summary */}
            {transferCalculations && (
              <div className='rounded-lg border bg-muted/30 p-4'>
                <h4 className='mb-3 font-semibold'>Billing Preview</h4>

                {/* Preview for 'no_change' billing intent */}
                {billingIntent === BillingIntent.NO_CHANGE && (
                  <div className='space-y-3 text-sm'>
                    <p className='text-muted-foreground'>
                      {transferCalculations.previewSummary.description}
                    </p>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span className='text-muted-foreground'>
                          Old Class:
                        </span>
                        <span className='font-medium'>
                          {transferCalculations.previewSummary.oldClassImpact}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-muted-foreground'>
                          New Class:
                        </span>
                        <span className='font-medium'>
                          {studioDetails?.prorationEnabled !== false
                            ? transferCalculations.previewSummary.newClassImpact
                            : transferCalculations.previewSummary.newClassImpact.replace(
                                /\s*\(prorated.*?\)/gi,
                                '',
                              )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detailed summary for 'immediate' billing intent */}
                {billingIntent === BillingIntent.IMMEDIATE && (
                  <div className='space-y-2 text-sm'>
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Current Class Fee:
                      </span>
                      <span className='font-medium'>
                        {currencySymbol}
                        {transferCalculations.currentTuitionFee.toFixed(2)}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        New Class Fee:
                      </span>
                      <span className='font-medium'>
                        {currencySymbol}
                        {transferCalculations.newTuitionFee.toFixed(2)}
                      </span>
                    </div>
                    <div className='my-2 border-t' />
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Available Credit:
                      </span>
                      <span className='font-medium text-green-600'>
                        {currencySymbol}
                        {transferCalculations.availableCredit.toFixed(2)}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Credit Applied to New Class:
                      </span>
                      <span className='font-medium text-green-600'>
                        -{currencySymbol}
                        {transferCalculations.creditUsedForTransfer.toFixed(2)}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Credit Added to Wallet:
                      </span>
                      <span
                        className={cn(
                          'font-medium',
                          transferCalculations.creditToBeAddedToWallet > 0
                            ? 'text-blue-600'
                            : 'text-muted-foreground',
                        )}
                      >
                        +{currencySymbol}
                        {transferCalculations.creditToBeAddedToWallet.toFixed(
                          2,
                        )}
                      </span>
                    </div>
                    {transferCalculations.discountLines.length > 0 ? (
                      <>
                        <div className='my-2 border-t' />
                        {transferCalculations.discountLines.map((line) => (
                          <div
                            key={`${line.label}-${line.amount}`}
                            className='flex justify-between'
                          >
                            <span className='text-muted-foreground'>
                              {line.label}:
                            </span>
                            <span className='font-medium text-green-600'>
                              -{currencySymbol}
                              {line.amount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : null}
                    <div className='my-2 border-t' />
                    <div className='flex justify-between text-base'>
                      <span className='font-semibold'>Amount to Charge:</span>
                      <span
                        className={cn(
                          'font-bold',
                          transferCalculations.amountToCharge > 0
                            ? 'text-orange-600'
                            : 'text-green-600',
                        )}
                      >
                        {currencySymbol}
                        {transferCalculations.amountToCharge.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setShowTransferDialog(false);
              }}
            >
              Cancel
            </Button>

            <Button
              type='button'
              disabled={
                transferClassMutation.isPending ||
                !transferCalculations ||
                !!remainingCreditError ||
                (isV2Transfer &&
                  !skipPaymentMethodWatch &&
                  (isLoadingEnrollmentPms || !resolvedPaymentMethodId))
              }
              onClick={handleSubmit(handleTransferSaveClick)}
            >
              {transferClassMutation.isPending ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ageRangeWarningOpen && ageRangeWarningClass && (
        <AgeRangeWarningDialog
          open={ageRangeWarningOpen}
          onOpenChange={handleAgeRangeWarningClose}
          onConfirm={handleAgeRangeWarningConfirm}
          studentName={studentName}
          studentAge={studentAge}
          classAgeRange={{
            startYear: ageRangeWarningClass.startAgeYear,
            startMonth: ageRangeWarningClass.startAgeMonth,
            endYear: ageRangeWarningClass.endAgeYear,
            endMonth: ageRangeWarningClass.endAgeMonth,
          }}
          confirmLabel='Proceed with Transfer'
          proceedMessage='Do you want to proceed with the transfer anyway?'
        />
      )}
    </>
  );
}
